<template>
  <div class="mb-12 mt-10">
    <div
      class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 border border-emerald-200 mb-4 shadow-sm"
    >
      <iconify-icon icon="solar:box-minimalistic-bold" class="text-emerald-600"></iconify-icon>
      <span class="text-xs font-semibold text-emerald-700 uppercase tracking-widest">
        {{ t('home.features.premium.badge') }}
      </span>
    </div>
    <h2 class="text-3xl lg:text-4xl font-serif text-[#1c4848] font-semibold tracking-tight">
      {{ t('home.features.premium.title') }}
    </h2>
    <p class="text-[#1c4848]/60 font-medium mt-2">
      {{ t('home.features.premium.subtitle') }}
    </p>
  </div>

  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-[240px]">
    <div
      v-reveal
      class="md:col-span-2 lg:col-span-2 bg-white rounded-[40px] p-8 border border-[#1c4848]/5 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
    >
      <div class="absolute right-0 top-0 w-40 h-full bg-gradient-to-l from-sky-50 to-transparent pointer-events-none"></div>
      <div class="relative z-10">
        <iconify-icon icon="solar:augmented-reality-linear" class="text-3xl text-sky-500 mb-4"></iconify-icon>
        <h3 class="text-2xl font-serif text-[#1c4848] font-semibold mb-2 tracking-tight">
          {{ t('home.features.premium.arQibla.title') }}
        </h3>
        <p class="text-sm text-[#1c4848]/60 font-medium max-w-sm">
          {{ t('home.features.premium.arQibla.body') }}
        </p>
      </div>
      <div class="relative z-10 flex items-center gap-2 mt-4 text-sky-600 font-semibold text-xs tracking-wide uppercase">
        <iconify-icon icon="solar:camera-linear" class="text-lg"></iconify-icon>
        {{ t('home.features.premium.arQibla.cta') }}
      </div>
    </div>

    <div
      v-reveal="{ delayMs: 80 }"
      class="bg-emerald-50 rounded-[40px] p-8 border border-emerald-100 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
    >
      <div
        class="absolute -right-4 -bottom-4 w-32 h-32 bg-white/50 border-2 border-emerald-200 rounded-3xl rotate-12 scale-110 opacity-50 group-hover:rotate-6 transition-transform flex items-center justify-center"
      >
        <div class="w-16 h-1 bg-emerald-400 rounded-full rotate-45"></div>
      </div>
      <div class="relative z-10">
        <iconify-icon icon="solar:user-rounded-linear" class="text-3xl text-emerald-600 mb-4"></iconify-icon>
        <h3 class="text-xl font-serif text-[#1c4848] font-semibold mb-2 tracking-tight">
          {{ t('home.features.premium.postureGuide.title') }}
        </h3>
        <p class="text-sm text-[#1c4848]/60 font-medium">
          {{ t('home.features.premium.postureGuide.body') }}
        </p>
      </div>
    </div>

    <div
      v-reveal="{ delayMs: 140 }"
      class="bg-rose-50 rounded-[40px] p-8 border border-rose-100 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
    >
      <div class="absolute right-6 top-6 flex gap-1 items-end h-6 opacity-60">
        <div class="w-1 bg-rose-400 rounded-full h-[30%] music-bar"></div>
        <div class="w-1 bg-rose-400 rounded-full h-[80%] music-bar"></div>
        <div class="w-1 bg-rose-400 rounded-full h-[50%] music-bar"></div>
        <div class="w-1 bg-rose-400 rounded-full h-[100%] music-bar"></div>
      </div>
      <div class="relative z-10">
        <iconify-icon icon="solar:global-linear" class="text-3xl text-rose-500 mb-4"></iconify-icon>
        <h3 class="text-xl font-serif text-[#1c4848] font-semibold mb-2 tracking-tight">
          {{ t('home.features.premium.liveKhutbah.title') }}
        </h3>
        <p class="text-sm text-[#1c4848]/60 font-medium">
          {{ t('home.features.premium.liveKhutbah.body') }}
        </p>
      </div>
    </div>

    <div
      v-reveal="{ delayMs: 200 }"
      class="md:col-span-2 lg:col-span-2 bg-[#1c4848] text-white rounded-[40px] p-8 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
    >
      <div class="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay"></div>
      <div class="absolute right-0 bottom-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div class="relative z-10 flex flex-col md:flex-row gap-6 items-center justify-between h-full">
        <div class="max-w-xs">
          <iconify-icon icon="solar:check-read-linear" class="text-3xl text-emerald-400 mb-4"></iconify-icon>
          <h3 class="text-2xl font-serif text-white font-semibold mb-2 tracking-tight">
            {{ t('home.features.premium.tajweed.title') }}
          </h3>
          <p class="text-sm text-white/60 font-medium">
            {{ t('home.features.premium.tajweed.body') }}
          </p>
        </div>
        <div
          class="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center shrink-0 relative group-hover:scale-105 transition-transform cursor-pointer"
        >
          <div class="absolute inset-0 rounded-full border border-emerald-400/50 scale-125 animate-pulse-ring"></div>
          <iconify-icon icon="solar:microphone-3-bold" class="text-3xl text-emerald-400"></iconify-icon>
        </div>
      </div>
    </div>

    <div
      v-reveal="{ delayMs: 260 }"
      class="md:col-span-2 lg:col-span-2 bg-[#fafbfc] rounded-[40px] p-8 border border-[#1c4848]/5 relative overflow-hidden group hover:shadow-xl transition-all duration-500 flex flex-col justify-between"
    >
      <div class="relative z-10">
        <iconify-icon icon="solar:widget-add-linear" class="text-3xl text-[#1c4848] mb-4"></iconify-icon>
        <h3 class="text-2xl font-serif text-[#1c4848] font-semibold mb-2 tracking-tight">
          {{ t('home.features.premium.essentials.title') }}
        </h3>
        <p class="text-sm text-[#1c4848]/60 font-medium">
          {{ t('home.features.premium.essentials.body') }}
        </p>
      </div>
      <div class="relative z-10 flex flex-wrap gap-3 mt-4">
        <div
          class="bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm hover:-translate-y-1 transition-transform"
        >
          <div class="text-[#1c4848] bg-[#1c4848]/5 w-8 h-8 rounded-lg flex items-center justify-center relative overflow-hidden">
            <iconify-icon icon="solar:barcode-linear"></iconify-icon>
            <div class="absolute w-full h-0.5 bg-emerald-500 animate-scan"></div>
          </div>
          <span class="text-sm font-semibold text-[#1c4848]">
            {{ t('home.features.premium.essentials.halalScanner') }}
          </span>
        </div>
        <div
          class="bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm hover:-translate-y-1 transition-transform delay-75"
        >
          <div class="text-[#1c4848] bg-[#1c4848]/5 w-8 h-8 rounded-lg flex items-center justify-center">
            <iconify-icon icon="solar:hand-stars-linear"></iconify-icon>
          </div>
          <span class="text-sm font-semibold text-[#1c4848]">
            {{ t('home.features.premium.essentials.tapTasbih') }}
          </span>
        </div>
        <div
          class="bg-white border border-[#1c4848]/10 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-sm hover:-translate-y-1 transition-transform delay-150"
        >
          <div class="text-[#1c4848] bg-[#1c4848]/5 w-8 h-8 rounded-lg flex items-center justify-center">
            <iconify-icon icon="solar:calculator-minimalistic-linear"></iconify-icon>
          </div>
          <span class="text-sm font-semibold text-[#1c4848]">
            {{ t('home.features.premium.essentials.zakat') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
</script>

